import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getPGProperties,
  getPGPropertiesAmenities,
  getPGPropertiesBId,
  createPgProperties,
  updatePgProperties,
  togglePgPropertiesStatus,
  getPgPricingById,
  updatePgPricingById,
} from "../../../services/propertyOwner/propertyOwnerServices";

import { getMasterPincode } from "../../../services/admin/adminServices";

const INITIAL_ADD_FORM = {
  name: "",
  propertyType: "Boys",
  totalFloors: 1,
  messAvailable: false,
  address: "",
  area: "",
  city: "",
  stateId: 0,
  stateName: "",
  pincode: "",
  description: "",
  houseRules: "",
  amenityIds: [],
  photos: [],
  video: null,
};

export default function PgProperties() {
  const [pgData, setPgData] = useState(null);
  const [listLoading, setListLoading] = useState(true);

  const [isOpenAddModal, setIsOpenAddModal] = useState(false);
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [isOpenPricingModal, setIsOpenPricingModal] = useState(false);

  const [amenitiesList, setAmenitiesList] = useState([]);

  const [addForm, setAddForm] = useState(INITIAL_ADD_FORM);
  const [addErrors, setAddErrors] = useState({});
  const [addLoading, setAddLoading] = useState(false);
  const [addPhotoPreviews, setAddPhotoPreviews] = useState([]);

  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editPhotoPreviews, setEditPhotoPreviews] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);

  const [pricingData, setPricingData] = useState([]);
  const [pricingProperty, setPricingProperty] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);

  useEffect(() => {
    fetchPgProperties();
  }, []);

  const fetchPgProperties = async () => {
    setListLoading(true);
    try {
      const res = await getPGProperties();
      if (res.statusCode === 1) setPgData(res.data);
    } catch (err) {
      console.log(
        err.response?.data?.message || "Failed to fetch PG properties",
      );
    } finally {
      setListLoading(false);
    }
  };

  const fetchAmenities = async () => {
    try {
      const res = await getPGPropertiesAmenities();
      if (res.statusCode === 1) setAmenitiesList(res.data.amenities || []);
    } catch (err) {
      console.log("Failed to fetch amenities");
    }
  };

  const formatDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      : "—";

  // ── Add Modal ──
  const openAddModal = () => {
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setAddPhotoPreviews([]);
    fetchAmenities();
    setIsOpenAddModal(true);
  };

  const closeAddModal = () => {
    setIsOpenAddModal(false);
    setAddForm(INITIAL_ADD_FORM);
    setAddErrors({});
    setAddPhotoPreviews([]);
  };

  const handleAddPincodeChange = async (val) => {
    setAddForm((prev) => ({ ...prev, pincode: val }));
    setAddErrors((prev) => ({ ...prev, pincode: "" }));
    if (val.length === 6) {
      try {
        const res = await getMasterPincode(val);
        if (res.statusCode === 1) {
          setAddForm((prev) => ({
            ...prev,
            area: res.data.areaName || "",
            city: res.data.district || "",
            stateId: Number(res.data.stateId),
            stateName: res.data.stateName,
          }));
        }
      } catch {
        console.log("Pincode not found");
      }
    }
  };

  const handleAddAmenityToggle = (id) => {
    setAddForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(id)
        ? prev.amenityIds.filter((a) => a !== id)
        : [...prev.amenityIds, id],
    }));
  };

  const handleAddPhotos = (e) => {
    const files = Array.from(e.target.files);
    setAddForm((prev) => ({ ...prev, photos: files }));
    setAddPhotoPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const handleAddVideo = (e) => {
    setAddForm((prev) => ({ ...prev, video: e.target.files[0] || null }));
  };

  const validateAdd = () => {
    const e = {};
    if (!addForm.name) e.name = "PG Name is required.";
    if (!addForm.pincode) e.pincode = "Pincode is required.";
    setAddErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddSubmit = async () => {
    if (!validateAdd()) return;
    setAddLoading(true);
    try {
      const fd = new FormData();
      fd.append("Name", addForm.name);
      fd.append("PropertyType", addForm.propertyType);
      fd.append("TotalFloors", addForm.totalFloors);
      fd.append("MessAvailable", addForm.messAvailable);
      fd.append("Address", addForm.address);
      fd.append("City", addForm.city);
      fd.append("StateId", addForm.stateId);
      fd.append("Pincode", addForm.pincode);
      fd.append("Description", addForm.description);
      fd.append("HouseRules", addForm.houseRules);
      if (addForm.amenityIds.length > 0) {
        addForm.amenityIds.forEach((id) => fd.append("amenityIds", id));
      } else {
        fd.append("amenityIds", 0);
      }
      addForm.photos.forEach((f) => fd.append("photos", f));
      if (addForm.video) fd.append("video", addForm.video);

      const res = await createPgProperties(fd);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeAddModal();
        fetchPgProperties();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit Modal ──
  const openEditModal = async (id) => {
    try {
      const res = await getPGPropertiesBId(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedProperty(d);
        setEditForm({
          name: d.name || "",
          propertyType: d.propertyType || "Boys",
          totalFloors: d.totalFloors || 1,
          messAvailable: d.messAvailable || false,
          address: d.address || "",
          area: d.areaName || "",
          city: d.city || "",
          stateId: d.stateId || 0,
          stateName: d.stateName || "",
          pincode: d.pincode || "",
          description: d.description || "",
          houseRules: d.houseRules || "",
          amenityIds: d.amenityIds
            ? d.amenityIds.split(",").map(Number).filter(Boolean)
            : [],
          photos: [],
          video: null,
        });
        setExistingPhotos(d.photosJson ? JSON.parse(d.photosJson) : []);
        setEditPhotoPreviews([]);
        setEditErrors({});
        fetchAmenities();
        setIsOpenEditModal(true);
      }
    } catch (err) {
      toast.error("Failed to load property details.");
    }
  };

  const closeEditModal = () => {
    setIsOpenEditModal(false);
    setSelectedProperty(null);
    setEditForm({});
    setEditErrors({});
    setEditPhotoPreviews([]);
    setExistingPhotos([]);
  };

  const handleEditPincodeChange = async (val) => {
    setEditForm((prev) => ({ ...prev, pincode: val }));
    setEditErrors((prev) => ({ ...prev, pincode: "" }));
    if (val.length === 6) {
      try {
        const res = await getMasterPincode(val);
        if (res.statusCode === 1) {
          setEditForm((prev) => ({
            ...prev,
            city: res.data.areaName || prev.city,
            stateId: Number(res.data.stateId),
            stateName: res.data.stateName,
          }));
        }
      } catch {
        console.log("Pincode not found");
      }
    }
  };

  const handleEditAmenityToggle = (id) => {
    setEditForm((prev) => ({
      ...prev,
      amenityIds: prev.amenityIds.includes(id)
        ? prev.amenityIds.filter((a) => a !== id)
        : [...prev.amenityIds, id],
    }));
  };

  const handleEditPhotos = (e) => {
    const files = Array.from(e.target.files);
    setEditForm((prev) => ({ ...prev, photos: files }));
    setEditPhotoPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  const handleEditVideo = (e) => {
    setEditForm((prev) => ({ ...prev, video: e.target.files[0] || null }));
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.name) e.name = "PG Name is required.";
    if (!editForm.pincode) e.pincode = "Pincode is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const fd = new FormData();
      fd.append("Name", editForm.name);
      fd.append("PropertyType", editForm.propertyType);
      fd.append("TotalFloors", editForm.totalFloors);
      fd.append("MessAvailable", editForm.messAvailable);
      fd.append("Address", editForm.address);
      fd.append("City", editForm.city);
      fd.append("StateId", editForm.stateId);
      fd.append("Pincode", editForm.pincode);
      fd.append("Description", editForm.description);
      fd.append("HouseRules", editForm.houseRules);
      if (editForm.amenityIds.length > 0) {
        editForm.amenityIds.forEach((id) => fd.append("amenityIds", id));
      } else {
        fd.append("amenityIds", 0);
      }
      editForm.photos.forEach((f) => fd.append("photos", f));
      if (editForm.video) fd.append("video", editForm.video);

      const res = await updatePgProperties(selectedProperty.pgPropertyId, fd);
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModal();
        fetchPgProperties();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Toggle ──
  const handleToggleStatus = async (id) => {
    try {
      const res = await togglePgPropertiesStatus(id);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchPgProperties();
      }
    } catch (err) {
      toast.error("Failed to toggle status.");
    }
  };

  // ── Pricing Modal ──
  const openPricingModal = async (p) => {
    setPricingProperty(p);
    setPricingLoading(true);
    setIsOpenPricingModal(true);
    try {
      const res = await getPgPricingById(p.pgPropertyId);
      if (res.statusCode === 1) setPricingData(res.data);
    } catch (err) {
      toast.error("Failed to load pricing.");
    } finally {
      setPricingLoading(false);
    }
  };

  const closePricingModal = () => {
    setIsOpenPricingModal(false);
    setPricingProperty(null);
    setPricingData([]);
  };

  const handlePricingChange = (sharingTypeId, field, value) => {
    setPricingData((prev) =>
      prev.map((item) =>
        item.sharingTypeId === sharingTypeId
          ? { ...item, [field]: Number(value) }
          : item,
      ),
    );
  };

  const handlePricingSubmit = async () => {
    try {
      const payload = {
        pricing: pricingData.map((item) => ({
          sharingTypeId: item.sharingTypeId,
          monthlyRate: item.monthlyRate,
          dailyRate: item.dailyRate,
        })),
      };
      const res = await updatePgPricingById(
        pricingProperty.pgPropertyId,
        payload,
      );
      if (res.statusCode === 1) {
        toast.success(res.message);
        closePricingModal();
        fetchPgProperties();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title" id="page-title">
              PG Properties
            </div>
            <div className="page-sub" id="page-sub">
              Manage your PG properties
            </div>
          </div>
          <div className="topbar-right" id="topbar-right">
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add PG Property
            </button>
          </div>
        </div>

        <div className="content" id="content-area">
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">🏠</div>
              <div className="stat-label">Total PG Properties</div>
              <div className="stat-value">{pgData?.totalProperties ?? 0}</div>
              <div className="stat-delta delta-neu">Registered</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Active</div>
              <div className="stat-value">{pgData?.active ?? 0}</div>
              <div className="stat-delta delta-up">Operational</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🛏</div>
              <div className="stat-label">Total Beds</div>
              <div className="stat-value">{pgData?.totalBeds ?? 0}</div>
              <div className="stat-delta delta-neu">Across all PGs</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🍽</div>
              <div className="stat-label">With Mess</div>
              <div className="stat-value">{pgData?.withMess ?? 0}</div>
              <div className="stat-delta delta-neu">Food service</div>
            </div>
          </div>

          <div className="card">
            <div className="flex-between mb12">
              <div className="card-title" style={{ margin: 0 }}>
                All PG Properties
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>PG Name</th>
                    <th>Type</th>
                    <th>Location</th>
                    <th>Mess</th>
                    <th>Floors</th>
                    <th>Beds</th>
                    <th>Pricing</th>
                    <th>Added On</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 11 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: j === 1 ? "80%" : "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pgData?.properties?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={11}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No PG properties found.
                      </td>
                    </tr>
                  ) : (
                    pgData?.properties?.map((p, idx) => (
                      <tr key={p.pgPropertyId}>
                        <td className="sn-col">{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{p.name}</div>
                          <div
                            style={{
                              fontSize: 10,
                              color: "var(--text-tertiary)",
                            }}
                          >
                            {[p.address, p.city].filter(Boolean).join(", ")}
                          </div>
                        </td>
                        <td>
                          <span className="badge badge-info">
                            {p.propertyType ?? "—"}
                          </span>
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {[p.city, p.stateName].filter(Boolean).join(", ")}
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {p.messAvailable ? (
                            <span className="badge badge-teal">✅ Yes</span>
                          ) : (
                            <span className="badge badge-gray">✕ No</span>
                          )}
                        </td>
                        <td style={{ fontSize: 11 }}>
                          {p.totalFloors ?? "—"} floors
                        </td>
                        <td>
                          <div style={{ fontSize: 11 }}>
                            <span style={{ color: "var(--green)" }}>
                              {p.occupiedBeds} occ
                            </span>
                            {" / "}
                            <span style={{ color: "var(--amber)" }}>
                              {p.totalBeds - p.occupiedBeds} vacant
                            </span>
                            {" of "}
                            <strong>{p.totalBeds}</strong> beds
                          </div>
                        </td>
                        <td
                          style={{
                            fontSize: 11,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {p.pricingSummary || "—"}
                        </td>
                        <td
                          style={{
                            fontSize: 10,
                            color: "var(--text-tertiary)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatDate(p.createdAt)}
                        </td>
                        <td>
                          <span
                            className={`badge ${p.isActive ? "badge-active" : "badge-inactive"}`}
                          >
                            {p.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="td-actions">
                            <span
                              className="action-icon"
                              onClick={() => openEditModal(p.pgPropertyId)}
                            >
                              <span className="tip">Edit</span>✎
                            </span>
                            <span
                              className="action-icon"
                              onClick={() => openPricingModal(p)}
                            >
                              <span className="tip">Pricing</span>💲
                            </span>
                            {p.isActive ? (
                              <span
                                className="action-icon red"
                                onClick={() =>
                                  handleToggleStatus(p.pgPropertyId)
                                }
                              >
                                <span className="tip">Deactivate</span>⊗
                              </span>
                            ) : (
                              <span
                                className="action-icon green"
                                onClick={() =>
                                  handleToggleStatus(p.pgPropertyId)
                                }
                              >
                                <span className="tip">Activate</span>✓
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isOpenAddModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 820, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Add New PG Property</div>
              <button className="modal-close" onClick={closeAddModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group" style={{ gridColumn: "1/-1" }}>
                  <label>PG Name *</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => {
                      setAddForm((p) => ({ ...p, name: e.target.value }));
                      setAddErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder="e.g. Sunrise PG Boys Block"
                    style={addErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Property Type</label>
                  <select
                    value={addForm.propertyType}
                    onChange={(e) =>
                      setAddForm((p) => ({
                        ...p,
                        propertyType: e.target.value,
                      }))
                    }
                  >
                    <option>Boys</option>
                    <option>Girls</option>
                    <option>Co-Living</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Total Floors</label>
                  <input
                    type="number"
                    value={addForm.totalFloors}
                    min={1}
                    onChange={(e) =>
                      setAddForm((p) => ({
                        ...p,
                        totalFloors: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div
                  className="form-group"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    paddingTop: 18,
                  }}
                >
                  <label style={{ margin: 0, whiteSpace: "nowrap" }}>
                    Mess Available
                  </label>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={addForm.messAvailable}
                      onChange={(e) =>
                        setAddForm((p) => ({
                          ...p,
                          messAvailable: e.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={addForm.address}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, address: e.target.value }))
                    }
                    placeholder="Street address"
                  />
                </div>
                <div className="form-group">
                  <label>Area</label>
                  <input
                    type="text"
                    value={addForm.area}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, area: e.target.value }))
                    }
                    placeholder="Auto-filled from pincode"
                  />
                </div>
                <div className="form-group">
                  <label>City / District</label>
                  <input
                    type="text"
                    value={addForm.city}
                    onChange={(e) =>
                      setAddForm((p) => ({ ...p, city: e.target.value }))
                    }
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={addForm.stateName}
                    readOnly
                    placeholder="Auto-filled"
                    style={{ background: "var(--bg-secondary)" }}
                  />
                </div>
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    value={addForm.pincode}
                    maxLength={6}
                    onChange={(e) => handleAddPincodeChange(e.target.value)}
                    placeholder="Pincode"
                    style={addErrors.pincode ? { borderColor: "#dc2626" } : {}}
                  />
                  {addErrors.pincode && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.pincode}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group mb12">
                <label>Description</label>
                <textarea
                  value={addForm.description}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, description: e.target.value }))
                  }
                  style={{ minHeight: 80 }}
                  placeholder="About this PG..."
                />
              </div>

              <div className="form-group mb12">
                <label>
                  House Rules{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (plain text)
                  </span>
                </label>
                <textarea
                  value={addForm.houseRules}
                  onChange={(e) =>
                    setAddForm((p) => ({ ...p, houseRules: e.target.value }))
                  }
                  style={{ minHeight: 55 }}
                  placeholder="e.g. No smoking. Visitors till 9 PM."
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  📷 PG Photos{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Multiple)
                  </span>
                </label>
                {addPhotoPreviews.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {addPhotoPreviews.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--blue)",
                        }}
                      />
                    ))}
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--blue-light)",
                    color: "var(--blue)",
                    border: "1px solid var(--blue)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  📁 Choose Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleAddPhotos}
                  />
                </label>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  🎥 PG Video{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Optional)
                  </span>
                </label>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--purple-light)",
                    color: "var(--purple)",
                    border: "1px solid var(--purple)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  🎬 Choose Video
                  <input
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={handleAddVideo}
                  />
                </label>
                {addForm.video && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {addForm.video.name}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {amenitiesList.map((a) => (
                    <label
                      key={a.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "5px 8px",
                        borderRadius: 6,
                        background: addForm.amenityIds.includes(a.amenityId)
                          ? "var(--blue-light)"
                          : "transparent",
                        border: `0.5px solid ${addForm.amenityIds.includes(a.amenityId) ? "var(--blue)" : "var(--border-light)"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={addForm.amenityIds.includes(a.amenityId)}
                        onChange={() => handleAddAmenityToggle(a.amenityId)}
                      />
                      <span>{a.icon}</span> {a.amenityName}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeAddModal}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "💾 Save PG Property"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isOpenEditModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 820, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">
                Edit PG Property — {selectedProperty?.name}
              </div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group" style={{ gridColumn: "1/-1" }}>
                  <label>PG Name *</label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => {
                      setEditForm((p) => ({ ...p, name: e.target.value }));
                      setEditErrors((p) => ({ ...p, name: "" }));
                    }}
                    placeholder="e.g. Sunrise PG Boys Block"
                    style={editErrors.name ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.name && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.name}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Property Type</label>
                  <select
                    value={editForm.propertyType}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        propertyType: e.target.value,
                      }))
                    }
                  >
                    <option>Boys</option>
                    <option>Girls</option>
                    <option>Co-Living</option>
                    <option>Family</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Total Floors</label>
                  <input
                    type="number"
                    value={editForm.totalFloors}
                    min={1}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        totalFloors: Number(e.target.value),
                      }))
                    }
                  />
                </div>
                <div
                  className="form-group"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    paddingTop: 18,
                  }}
                >
                  <label style={{ margin: 0, whiteSpace: "nowrap" }}>
                    Mess Available
                  </label>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={editForm.messAvailable}
                      onChange={(e) =>
                        setEditForm((p) => ({
                          ...p,
                          messAvailable: e.target.checked,
                        }))
                      }
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr 1fr",
                  gap: 12,
                  marginBottom: 14,
                }}
              >
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={editForm.address}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, address: e.target.value }))
                    }
                    placeholder="Street address"
                  />
                </div>

                <div className="form-group">
                  <label>Area</label>
                  <input
                    type="text"
                    value={editForm.area || ""}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, area: e.target.value }))
                    }
                    placeholder="Auto-filled from pincode"
                  />
                </div>
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={editForm.city}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, city: e.target.value }))
                    }
                    placeholder="City"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>

                  <input
                    type="text"
                    value={editForm.stateName || ""}
                    readOnly
                    placeholder="Auto-filled"
                    style={{ background: "var(--bg-secondary)" }}
                  />
                </div>
                <div className="form-group">
                  <label>Pincode *</label>
                  <input
                    type="text"
                    value={editForm.pincode}
                    maxLength={6}
                    onChange={(e) => handleEditPincodeChange(e.target.value)}
                    placeholder="Pincode"
                    style={editErrors.pincode ? { borderColor: "#dc2626" } : {}}
                  />
                  {editErrors.pincode && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.pincode}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group mb12">
                <label>Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, description: e.target.value }))
                  }
                  style={{ minHeight: 80 }}
                  placeholder="About this PG..."
                />
              </div>

              <div className="form-group mb12">
                <label>
                  House Rules{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (plain text)
                  </span>
                </label>
                <textarea
                  value={editForm.houseRules}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, houseRules: e.target.value }))
                  }
                  style={{ minHeight: 55 }}
                  placeholder="e.g. No smoking. Visitors till 9 PM."
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  📷 PG Photos{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Multiple)
                  </span>
                </label>
                {existingPhotos.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {existingPhotos.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--border-mid)",
                        }}
                      />
                    ))}
                  </div>
                )}
                {editPhotoPreviews.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginBottom: 8,
                    }}
                  >
                    {editPhotoPreviews.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt=""
                        style={{
                          width: 60,
                          height: 60,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "0.5px solid var(--blue)",
                        }}
                      />
                    ))}
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--blue-light)",
                    color: "var(--blue)",
                    border: "1px solid var(--blue)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  📁 Choose Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleEditPhotos}
                  />
                </label>
              </div>

              <div style={{ marginBottom: 14 }}>
                <label
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  🎥 PG Video{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (Optional)
                  </span>
                </label>
                {selectedProperty?.videoUrl && !editForm.video && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--text-secondary)",
                      marginBottom: 6,
                    }}
                  >
                    Current:{" "}
                    <a
                      href={selectedProperty.videoUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: "var(--blue)" }}
                    >
                      View Video
                    </a>
                  </div>
                )}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "7px 13px",
                    background: "var(--purple-light)",
                    color: "var(--purple)",
                    border: "1px solid var(--purple)",
                    borderRadius: 6,
                    fontSize: 12,
                    cursor: "pointer",
                    fontWeight: 600,
                    width: "fit-content",
                  }}
                >
                  🎬 Choose Video
                  <input
                    type="file"
                    accept="video/*"
                    style={{ display: "none" }}
                    onChange={handleEditVideo}
                  />
                </label>
                {editForm.video && (
                  <span
                    style={{
                      fontSize: 11,
                      color: "#9ca3af",
                      marginTop: 4,
                      display: "block",
                    }}
                  >
                    {editForm.video.name}
                  </span>
                )}
              </div>

              <div className="form-group">
                <label>Amenities</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(4,1fr)",
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {amenitiesList.map((a) => (
                    <label
                      key={a.amenityId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 12,
                        cursor: "pointer",
                        padding: "5px 8px",
                        borderRadius: 6,
                        background: editForm.amenityIds?.includes(a.amenityId)
                          ? "var(--blue-light)"
                          : "transparent",
                        border: `0.5px solid ${editForm.amenityIds?.includes(a.amenityId) ? "var(--blue)" : "var(--border-light)"}`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          editForm.amenityIds?.includes(a.amenityId) || false
                        }
                        onChange={() => handleEditAmenityToggle(a.amenityId)}
                      />
                      <span>{a.icon}</span> {a.amenityName}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "💾 Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pricing Modal ── */}
      {isOpenPricingModal && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 620, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">₲ Bed Type Pricing</div>
                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                  {pricingProperty?.name}
                </div>
              </div>
              <button className="modal-close" onClick={closePricingModal}>
                ✕
              </button>
            </div>
            <div>
              <div className="info-box" style={{ margin: 14, fontSize: 12 }}>
                Set monthly and daily rent per bed type. Tenant billing mode
                (Monthly/Daily) is set individually in the Tenant List.
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "140px 1fr 1fr",
                  gap: 10,
                  padding: "10px 16px 6px",
                  background: "#f8fafc",
                  borderBottom: "0.5px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Bed Type
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Monthly Charge
                </div>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "var(--text-tertiary)",
                    textTransform: "uppercase",
                  }}
                >
                  Daily Charge
                </div>
              </div>
              {pricingLoading ? (
                <div
                  style={{
                    padding: "24px 0",
                    textAlign: "center",
                    color: "var(--text-tertiary)",
                    fontSize: 13,
                  }}
                >
                  Loading...
                </div>
              ) : (
                pricingData.map((item) => (
                  <div
                    key={item.sharingTypeId}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "140px 1fr 1fr",
                      gap: 10,
                      alignItems: "center",
                      padding: "14px 16px",
                      borderBottom: "0.5px solid #e5e7eb",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>
                        {item.sharingTypeName}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "var(--text-tertiary)" }}
                      >
                        Max {item.maxPersons} person(s)
                      </div>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                      <input
                        type="number"
                        value={item.monthlyRate}
                        min={0}
                        onChange={(e) =>
                          handlePricingChange(
                            item.sharingTypeId,
                            "monthlyRate",
                            e.target.value,
                          )
                        }
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "7px 9px",
                          border: "0.5px solid #d1d5db",
                          borderRadius: 6,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          whiteSpace: "nowrap",
                        }}
                      >
                        /mo
                      </span>
                    </div>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <span style={{ fontSize: 13, color: "#6b7280" }}>₹</span>
                      <input
                        type="number"
                        value={item.dailyRate}
                        min={0}
                        onChange={(e) =>
                          handlePricingChange(
                            item.sharingTypeId,
                            "dailyRate",
                            e.target.value,
                          )
                        }
                        style={{
                          flex: 1,
                          fontSize: 12,
                          padding: "7px 9px",
                          border: "0.5px solid #d1d5db",
                          borderRadius: 6,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 11,
                          color: "#9ca3af",
                          whiteSpace: "nowrap",
                        }}
                      >
                        /day
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={closePricingModal}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handlePricingSubmit}>
                💾 Save Pricing
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
